import {BrandingTemplate} from "../../entity/model/branding-template";
import BrandingTemplateStore from "../store/branding-template-store";
import {RequestBody} from "../../entity/core/request-body";
import {toast} from "react-toastify";
import {DEFAULT_BRANDING_TEMPLATES} from "../../shared/constants/branding-templates";

export class BrandingTemplateService {
    private templates: BrandingTemplate[] = [];
    private brandingTemplateStore: BrandingTemplateStore;

    constructor(brandingTemplateStore: BrandingTemplateStore) {
        this.brandingTemplateStore = brandingTemplateStore;
        this.loadTemplates();
    }

    async fetchTemplatesFromAPI(organizationId: string): Promise<BrandingTemplate[]> {
        await this.brandingTemplateStore.loadBrandingTemplates(organizationId);
        const apiTemplates = this.brandingTemplateStore.getBrandingTemplates();

        const transformedTemplates: BrandingTemplate[] = [];

        if (Array.isArray(apiTemplates)) {
            transformedTemplates.push(...apiTemplates.map((template: any, index: number) => ({
                id: template.id || template.templateId || `api-${Date.now()}-${index}`,
                name: template.templateName || template.name || 'Unnamed Template',
                companyName: template.companyName || '',
                companyAddress: template.companyAddress || '',
                phone: template.phone || '',
                email: template.email || '',
                website: template.website || '',
                logo: template.companyLogo || template.logo || '',
                footerText: template.footerText || ''
            })));
        }

        const hasDefault = transformedTemplates.some(t => t.id === 'default');
        if (!hasDefault) {
            transformedTemplates.unshift(...DEFAULT_BRANDING_TEMPLATES);
        }

        this.templates = transformedTemplates;
        this.saveTemplatesToStorage();

        return this.templates;
    }

    getTemplates(): BrandingTemplate[] {
        this.loadTemplates();
        return [...this.templates];
    }

    getTemplateById(templateId: string): BrandingTemplate | undefined {
        this.loadTemplates();
        return this.templates.find(template => template.id === templateId);
    }

    getCurrentTemplate(templateId: string): BrandingTemplate {
        this.loadTemplates();
        const template = this.getTemplateById(templateId);
        return template || this.templates[0];
    }

    async loadTemplatesForUser(organizationId: string): Promise<BrandingTemplate[]> {
        if (!organizationId) {
            const localTemplates = this.getTemplates();
            toast.info('Using local templates (no organization found)');
            return localTemplates;
        }

        const result = await this.fetchTemplatesFromAPI(organizationId);

        if (result.length > 0) {
            return result;
        } else {
            const localTemplates = this.getTemplates();
            toast.warning('Using local templates. Failed to fetch from server.');
            return localTemplates;
        }
    }

    async saveTemplateForUser(template: BrandingTemplate, organizationId: string): Promise<BrandingTemplate | undefined> {
        if (!organizationId) {
            toast.error('User organization information is missing');
            return;
        }

        return await this.saveTemplate(template, organizationId);
    }

    async saveTemplate(template: BrandingTemplate, organizationId: string): Promise<BrandingTemplate | undefined> {
        const payload = {
            templateName: template.name,
            companyName: template.companyName,
            companyAddress: template.companyAddress,
            phone: template.phone ?? '',
            email: template.email ?? '',
            website: template.website ?? '',
            companyLogo: template.logo ?? '',
            footerText: template.footerText ?? ''
        };

        const requestBody: RequestBody = {body: payload};

        const result = await this.brandingTemplateStore.createBrandingTemplate(organizationId, requestBody);

        if (typeof result === 'string') {
            toast.error(result);
            return;
        }

        const updatedTemplate: BrandingTemplate = {
            id: result._id ?? template.id,
            name: result.templateName ?? template.name,
            companyName: result.companyName ?? template.companyName,
            companyAddress: result.companyAddress ?? template.companyAddress,
            phone: result.phone ?? template.phone ?? '',
            email: result.email ?? template.email ?? '',
            website: result.website ?? template.website ?? '',
            logo: result.companyLogo ?? template.logo ?? '',
            footerText: result.footerText ?? template.footerText ?? ''
        };

        const existingIndex = this.templates.findIndex(t => t.id === updatedTemplate.id);
        if (existingIndex >= 0) {
            this.templates[existingIndex] = updatedTemplate;
        } else {
            this.templates.push(updatedTemplate);
        }

        this.saveTemplatesToStorage();
        toast.success('Template saved successfully');

        return updatedTemplate;
    }

    getBrandingFormValues(editingTemplate?: BrandingTemplate): any {
        return {
            name: editingTemplate?.name ?? '',
            companyName: editingTemplate?.companyName ?? '',
            companyAddress: editingTemplate?.companyAddress ?? '',
            phone: editingTemplate?.phone ?? '',
            email: editingTemplate?.email ?? '',
            website: editingTemplate?.website ?? '',
            logo: editingTemplate?.logo ?? '',
            footerText: editingTemplate?.footerText ?? ''
        };
    }

    createBrandingTemplate(values: any, editingTemplate?: BrandingTemplate): BrandingTemplate {
        return {
            id: editingTemplate?.id ?? `template-${Date.now()}`,
            name: values.name,
            companyName: values.companyName,
            companyAddress: values.companyAddress,
            phone: values.phone ?? '',
            email: values.email ?? '',
            website: values.website ?? '',
            logo: values.logo ?? '',
            footerText: values.footerText ?? ''
        };
    }

    private loadTemplates(): void {
        const storedTemplates = localStorage.getItem('brandingTemplates');
        if (storedTemplates) {
            this.templates = JSON.parse(storedTemplates);
        } else {
            this.initializeDefaultTemplates();
        }
    }

    private saveTemplatesToStorage(): void {
        localStorage.setItem('brandingTemplates', JSON.stringify(this.templates));
    }

    private initializeDefaultTemplates(): void {
        this.templates = [...DEFAULT_BRANDING_TEMPLATES];
        this.saveTemplatesToStorage();
    }
} 